import type { NewsListItem, NewsListView } from "@slfinrtl/ui-bff-routes"
import { deleteNewsItemRoute, getNewsListRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext, useEffect, useState } from "react"
import { useIntl } from "react-intl"
import type { MessageDescriptor } from "react-intl"
import { extractErrorMessage } from "src/common/errorHandling/extractErrorMessage/extractErrorMessage"
import { useErrorBanner } from "src/context/errorBanner/useErrorBanner"
import { getAxiosInstance } from "../../../dealershipList/common/getAxiosInstance/getAxiosInstance"
import { RequestContext } from "../../../dealershipList/requestContext/RequestContext"
import { newsListMessages as messages } from "../../components/newsList.messages"

interface UseNewsListReturn {
	deleteItem: (id: string) => Promise<void>
	/** undefined while the items are loading or could not be loaded */
	items: NewsListItem[] | undefined
}

export const useNewsList = (): UseNewsListReturn => {
	const { environment, getAuthorizationHeader } = useContext(RequestContext)
	const { showError } = useErrorBanner()
	const { formatMessage } = useIntl()
	const [items, setItems] = useState<NewsListItem[] | undefined>()

	const showRequestError = useCallback(
		(error: unknown, fallbackMessage: MessageDescriptor) => {
			const errorMessage = extractErrorMessage(error)
			showError(errorMessage.message ? errorMessage : { message: formatMessage(fallbackMessage) })
		},
		[formatMessage, showError],
	)

	useEffect(() => {
		let isCancelled = false

		getAxiosInstance({ environment, getAuthorizationHeader })
			.then((axiosInstance) =>
				axiosInstance[getNewsListRoute.method]<NewsListView>(getNewsListRoute.buildUrl({})),
			)
			.then(({ data }) => {
				if (!isCancelled) {
					setItems(data.items)
				}
			})
			.catch((error: unknown) => showRequestError(error, messages.errorLoadingEntries))

		return () => {
			isCancelled = true
		}
	}, [environment, getAuthorizationHeader, showRequestError])

	const deleteItem = useCallback(
		async (id: string) => {
			try {
				const axiosInstance = await getAxiosInstance({ environment, getAuthorizationHeader })
				await axiosInstance[deleteNewsItemRoute.method](deleteNewsItemRoute.buildUrl({ newsItemId: id }))
				setItems((prevItems) => prevItems?.filter((item) => item.id !== id))
			} catch (error) {
				showRequestError(error, messages.errorDeletingEntry)
			}
		},
		[environment, getAuthorizationHeader, showRequestError],
	)

	return { deleteItem, items }
}
